import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  Box,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { OpenInFull as OpenInFullIcon } from "@mui/icons-material";
import apiService from "../../services/api";
import { DailyMetricsGrowthAbsoluteChart } from "../ProgramAnalysis/DailyMetricsGrowthAbsoluteChart";
import {
  buildSingleContentInteractionAbsoluteChartPoints,
  buildSingleContentViewsAbsoluteChartPoints,
  formatDailyMetricsAxisDate,
  type DailyMetricPoint,
  type DailyMetricsAbsoluteGrowthPoint,
} from "../ProgramAnalysis/dailyMetricsChart";
import { toChileanDateString } from "../../utils/dateUtils";
import {
  LivestreamProgramPeriodChartDialog,
  type PeriodChartKind,
} from "./LivestreamProgramPeriodChartDialog";
import type { LivestreamDetailListItem } from "./LivestreamDetailRecentList";
import { LivestreamViewerDualLineChartBody } from "./LivestreamViewerDualLineChart";
import { LivestreamProgramViewerShapeChart } from "./LivestreamProgramViewerShapeChart";
import { LivestreamProgramViewerShapeDialog } from "./LivestreamProgramViewerShapeDialog";
import { SummarySection } from "./SummarySection";
import {
  formatLivestreamOnDemandViewsCount,
  formatLivestreamTableViewsCount,
  formatNumber,
  formatViewerIntegerFloor,
  LIVESTREAM_DETAIL_GC_TIME,
  LIVESTREAM_DETAIL_STALE_TIME,
  LIVESTREAM_DETAIL_SUMMARY_TOOLTIPS,
  LIVESTREAM_METRIC_LABELS,
} from "./utils";

export type LivestreamProgramPeriodSectionProps = {
  channelId: string;
  programId?: string;
  startDate: string;
  endDate: string;
  country?: string;
  category?: string;
  title?: string;
  /** Show the loaded layout with skeletons (e.g. page chrome before channel id is known). */
  forceLoading?: boolean;
  livestreams?: LivestreamDetailListItem[];
  variant?: "channel" | "program";
};

const CHART_HEIGHT = 200;
const EXPANDED_CHART_HEIGHT = 360;

const CHART_PAPER_SX = {
  p: 2,
  bgcolor: "background.paper",
  border: "1px solid",
  borderColor: "divider",
  minWidth: 0,
  overflow: "visible",
} as const;

function chileNoonIso(date: string): string {
  return `${date}T15:00:00.000Z`;
}

function stampChartDates(
  points: DailyMetricsAbsoluteGrowthPoint[],
  daily: DailyMetricPoint[],
): DailyMetricsAbsoluteGrowthPoint[] {
  const unused = daily.map((metric) => ({
    label: formatDailyMetricsAxisDate(metric.collectedAt),
    date: toChileanDateString(metric.collectedAt),
  }));
  return points.map((point) => {
    if (point.date) return point;
    const index = unused.findIndex((row) => row.label === point.label);
    const date = index >= 0 ? unused.splice(index, 1)[0]?.date : undefined;
    return { ...point, date };
  });
}

export const LivestreamProgramPeriodSection: React.FC<
  LivestreamProgramPeriodSectionProps
> = ({
  channelId,
  programId,
  startDate,
  endDate,
  country,
  category,
  title,
  forceLoading = false,
  livestreams = [],
  variant = "program",
}) => {
  const { t } = useTranslation("explorer");
  const theme = useTheme();
  const chartMain = theme.palette.podscope.main;
  const chartSecondary = theme.palette.podscope.light;
  const sectionTitle = title ?? t("programPeriodMetrics");
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [expandedChart, setExpandedChart] = useState<PeriodChartKind | null>(
    null,
  );
  const [viewerShapeOpen, setViewerShapeOpen] = useState(false);

  const openExpanded = useCallback((chart: PeriodChartKind, date?: string) => {
    setExpandedChart(chart);
    setSelectedDate(date ?? null);
  }, []);

  const closeExpanded = useCallback(() => {
    setExpandedChart(null);
    setSelectedDate(null);
  }, []);

  const { data, isFetching, isPlaceholderData, isLoading } = useQuery({
    queryKey: [
      "explorer-livestream-program-period-metrics",
      "v4",
      channelId,
      programId ?? "channel",
      startDate,
      endDate,
    ],
    queryFn: () =>
      programId
        ? apiService.getExplorerLivestreamProgramPeriodMetrics(
            channelId,
            programId,
            startDate,
            endDate,
          )
        : apiService.getExplorerLivestreamChannelPeriodMetrics(
            channelId,
            startDate,
            endDate,
          ),
    enabled:
      !forceLoading && Boolean(channelId && startDate && endDate),
    staleTime: LIVESTREAM_DETAIL_STALE_TIME,
    gcTime: LIVESTREAM_DETAIL_GC_TIME,
    placeholderData: keepPreviousData,
  });

  const { data: competing } = useQuery({
    queryKey: [
      "explorer-livestream-channel-competing",
      "v2",
      channelId,
      startDate,
      endDate,
    ],
    queryFn: () =>
      apiService.getExplorerLivestreamChannelCompeting(
        channelId,
        startDate,
        endDate,
      ),
    enabled:
      !forceLoading &&
      expandedChart !== null &&
      Boolean(channelId && startDate && endDate),
    staleTime: LIVESTREAM_DETAIL_STALE_TIME,
    gcTime: LIVESTREAM_DETAIL_GC_TIME,
  });

  const showViewerShape = variant === "program" && Boolean(programId);
  const {
    data: viewerShape,
    isFetching: viewerShapeFetching,
    isPlaceholderData: viewerShapeIsPlaceholder,
    isLoading: viewerShapeLoading,
  } = useQuery({
    queryKey: [
      "explorer-livestream-program-viewer-shape",
      "series-v1",
      programId,
      startDate,
      endDate,
      country,
      category,
      channelId,
    ],
    queryFn: () =>
      apiService.getExplorerLivestreamProgramViewerShape(
        programId!,
        startDate,
        endDate,
        {
          ...(country ? { country } : {}),
          ...(category ? { category } : {}),
          ...(channelId ? { channel: channelId } : {}),
        },
      ),
    enabled:
      showViewerShape &&
      !forceLoading &&
      Boolean(programId && startDate && endDate),
    staleTime: LIVESTREAM_DETAIL_STALE_TIME,
    gcTime: LIVESTREAM_DETAIL_GC_TIME,
    placeholderData: keepPreviousData,
  });

  const showLoading =
    forceLoading ||
    (isLoading && !data) ||
    (isFetching && isPlaceholderData);

  const dailyMetrics = useMemo<DailyMetricPoint[]>(
    () =>
      (data?.youtube.daily ?? []).map((point) => ({
        collectedAt: chileNoonIso(point.date),
        views: point.views,
        likes: point.likes,
        comments: point.comments,
      })),
    [data?.youtube.daily],
  );

  const concurrentData = useMemo(() => {
    const byDay = new Map<
      string,
      { weekStart: string; avgViewers: number; peakViewers: number; n: number }
    >();
    for (const episode of data?.live.episodes ?? []) {
      const weekStart = `${episode.date}T00:00:00.000Z`;
      const prev = byDay.get(episode.date);
      if (!prev) {
        byDay.set(episode.date, {
          weekStart,
          avgViewers: episode.avgViewers,
          peakViewers: episode.peakViewers,
          n: 1,
        });
        continue;
      }
      prev.avgViewers += episode.avgViewers;
      prev.peakViewers = Math.max(prev.peakViewers, episode.peakViewers);
      prev.n += 1;
    }
    return Array.from(byDay.values())
      .sort((left, right) => left.weekStart.localeCompare(right.weekStart))
      .map((row) => ({
        weekStart: row.weekStart,
        avgViewers: row.n > 0 ? Math.round(row.avgViewers / row.n) : 0,
        peakViewers: row.peakViewers,
      }));
  }, [data?.live.episodes]);

  const viewsChartData = useMemo(
    () =>
      stampChartDates(
        buildSingleContentViewsAbsoluteChartPoints(dailyMetrics, "calendar"),
        dailyMetrics,
      ),
    [dailyMetrics],
  );
  const interactionsChartData = useMemo(
    () =>
      stampChartDates(
        buildSingleContentInteractionAbsoluteChartPoints(dailyMetrics, "calendar"),
        dailyMetrics,
      ),
    [dailyMetrics],
  );

  const youtube = data?.youtube.summary;
  const live = data?.live.summary;
  const episodeCount = live?.episodeCount ?? 0;
  const showYoutubeCharts = showLoading || dailyMetrics.length > 0;
  const showLiveChart = showLoading || concurrentData.length > 0;
  const viewerShapePoints = viewerShape?.points ?? [];
  const showViewerShapeLoading =
    showViewerShape &&
    (forceLoading ||
      (viewerShapeLoading && !viewerShape) ||
      (viewerShapeFetching && viewerShapeIsPlaceholder));
  const showViewerShapeChart =
    showViewerShape &&
    (showViewerShapeLoading || viewerShapePoints.length > 0);
  const selectedViews = selectedDate
    ? viewsChartData.find((point) => point.date === selectedDate)
    : undefined;
  const selectedInteractions = selectedDate
    ? interactionsChartData.find((point) => point.date === selectedDate)
    : undefined;
  const selectedLive = selectedDate
    ? concurrentData.find((point) => point.weekStart.startsWith(selectedDate))
    : undefined;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h2" sx={{ mb: 2 }}>
        {sectionTitle}
      </Typography>

      <SummarySection
        isLoading={showLoading}
        valueColor={theme.palette.text.primary}
        cards={[
          {
            title: t("livestreams"),
            tooltip: LIVESTREAM_DETAIL_SUMMARY_TOOLTIPS.livestreams,
            value: episodeCount,
          },
          {
            title: LIVESTREAM_METRIC_LABELS.avgEsp,
            tooltip: LIVESTREAM_DETAIL_SUMMARY_TOOLTIPS.avgEsp,
            value: formatViewerIntegerFloor(live?.avgViewers ?? 0),
          },
          {
            title: LIVESTREAM_METRIC_LABELS.avgPeakEsp,
            tooltip: LIVESTREAM_DETAIL_SUMMARY_TOOLTIPS.avgPeakEsp,
            value: formatViewerIntegerFloor(live?.avgPeakViewers ?? 0),
          },
          {
            title: t("views"),
            tooltip: LIVESTREAM_DETAIL_SUMMARY_TOOLTIPS.viewsTotales,
            value:
              variant === "program"
                ? formatLivestreamTableViewsCount(youtube?.views)
                : formatLivestreamOnDemandViewsCount(youtube?.views),
          },
          {
            title: t("interactions"),
            tooltip: t("tooltip.interactionsSum"),
            value: formatNumber(youtube?.interactions ?? 0),
          },
          {
            title: t("likes"),
            tooltip: t("tooltip.episodeLikes"),
            value: formatNumber(youtube?.likes ?? 0),
          },
          {
            title: t("comments"),
            tooltip: t("tooltip.episodeComments"),
            value: formatNumber(youtube?.comments ?? 0),
          },
        ]}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
          gap: 2,
        }}
      >
        <Paper elevation={0} sx={CHART_PAPER_SX}>
          <ChartCardHeader
            title={t("programMetricChartLiveViewers")}
            hint={t("programMetricChartLiveViewersHint")}
            expandLabel={t("expandChart")}
            canExpand={!showLoading && showLiveChart}
            onExpand={() => openExpanded("live")}
          />
          {showLoading ? (
            <Skeleton variant="rounded" height={CHART_HEIGHT} />
          ) : showLiveChart ? (
            <LivestreamViewerDualLineChartBody
              data={concurrentData}
              granularityTooltip={t("granularity.day")}
              peakColor={chartMain}
              avgColor={chartSecondary}
              height={CHART_HEIGHT}
              onDayClick={(date) => openExpanded("live", date)}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("noData")}
            </Typography>
          )}
        </Paper>
        <Paper elevation={0} sx={CHART_PAPER_SX}>
          <ChartCardHeader
            title={t("programMetricChartViews")}
            hint={t("programMetricChartViewsHint")}
            expandLabel={t("expandChart")}
            canExpand={!showLoading && showYoutubeCharts}
            onExpand={() => openExpanded("views")}
          />
          {showLoading ? (
            <Skeleton variant="rounded" height={CHART_HEIGHT} />
          ) : showYoutubeCharts ? (
            <DailyMetricsGrowthAbsoluteChart
              data={viewsChartData}
              height={CHART_HEIGHT}
              barColor={chartMain}
              lineColor={chartSecondary}
              incrementalLegend="Views ganadas"
              cumulativeLegend="Views totales"
              incrementalTooltipLabel="Views ganadas"
              cumulativeTooltipLabel="Views totales"
              onDayClick={(date) => openExpanded("views", date)}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("noData")}
            </Typography>
          )}
        </Paper>
        <Paper elevation={0} sx={CHART_PAPER_SX}>
          <ChartCardHeader
            title={t("programMetricChartInteractions")}
            hint={t("programMetricChartInteractionsHint")}
            expandLabel={t("expandChart")}
            canExpand={!showLoading && showYoutubeCharts}
            onExpand={() => openExpanded("interactions")}
          />
          {showLoading ? (
            <Skeleton variant="rounded" height={CHART_HEIGHT} />
          ) : showYoutubeCharts ? (
            <DailyMetricsGrowthAbsoluteChart
              data={interactionsChartData}
              height={CHART_HEIGHT}
              barColor={chartMain}
              lineColor={chartSecondary}
              incrementalLegend="Interacciones ganadas"
              cumulativeLegend="Interacciones totales"
              incrementalTooltipLabel="Interacciones ganadas"
              cumulativeTooltipLabel="Interacciones totales"
              onDayClick={(date) => openExpanded("interactions", date)}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("noData")}
            </Typography>
          )}
        </Paper>
      </Box>

      {showViewerShape ? (
        <Paper elevation={0} sx={{ ...CHART_PAPER_SX, mt: 2 }}>
          <ChartCardHeader
            title={t("programMetricChartViewerShape")}
            hint={t("programMetricChartViewerShapeHint")}
            expandLabel={t("expandChart")}
            canExpand={!showViewerShapeLoading && viewerShapePoints.length > 0}
            onExpand={() => setViewerShapeOpen(true)}
          />
          {showViewerShapeLoading ? (
            <Skeleton variant="rounded" height={CHART_HEIGHT} />
          ) : showViewerShapeChart ? (
            <LivestreamProgramViewerShapeChart
              data={viewerShapePoints}
              color={chartMain}
              height={CHART_HEIGHT}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("noData")}
            </Typography>
          )}
        </Paper>
      ) : null}

      <LivestreamProgramPeriodChartDialog
        open={expandedChart !== null}
        chart={expandedChart}
        onChartChange={setExpandedChart}
        chartLabels={{
          live: t("liveViewers"),
          views: t("views"),
          interactions: t("interactions"),
        }}
        availableCharts={[
          ...(concurrentData.length > 0 ? (["live"] as const) : []),
          ...(dailyMetrics.length > 0 ? (["views", "interactions"] as const) : []),
        ]}
        onClose={closeExpanded}
        selectedDate={selectedDate}
        views={selectedViews}
        interactions={selectedInteractions}
        live={selectedLive}
        livestreams={livestreams}
        showProgramName={variant === "channel"}
        competing={competing}
      >
        {expandedChart === "views" ? (
          <DailyMetricsGrowthAbsoluteChart
            data={viewsChartData}
            height={EXPANDED_CHART_HEIGHT}
            barColor={chartMain}
            lineColor={chartSecondary}
            incrementalLegend="Views ganadas"
            cumulativeLegend="Views totales"
            incrementalTooltipLabel="Views ganadas"
            cumulativeTooltipLabel="Views totales"
            onDayClick={setSelectedDate}
            hoverAnimate={false}
          />
        ) : expandedChart === "interactions" ? (
          <DailyMetricsGrowthAbsoluteChart
            data={interactionsChartData}
            height={EXPANDED_CHART_HEIGHT}
            barColor={chartMain}
            lineColor={chartSecondary}
            incrementalLegend="Interacciones ganadas"
            cumulativeLegend="Interacciones totales"
            incrementalTooltipLabel="Interacciones ganadas"
            cumulativeTooltipLabel="Interacciones totales"
            onDayClick={setSelectedDate}
            hoverAnimate={false}
          />
        ) : expandedChart === "live" ? (
          <LivestreamViewerDualLineChartBody
            data={concurrentData}
            granularityTooltip={t("granularity.day")}
            peakColor={chartMain}
            avgColor={chartSecondary}
            height={EXPANDED_CHART_HEIGHT}
            onDayClick={setSelectedDate}
            hoverAnimate={false}
          />
        ) : null}
      </LivestreamProgramPeriodChartDialog>

      {showViewerShape ? (
        <LivestreamProgramViewerShapeDialog
          open={viewerShapeOpen}
          title={t("programMetricChartViewerShape")}
          hint={t("programMetricChartViewerShapeHint")}
          points={viewerShapePoints}
          series={viewerShape?.series ?? []}
          onClose={() => setViewerShapeOpen(false)}
        />
      ) : null}
    </Box>
  );
};

function ChartCardHeader({
  title,
  hint,
  expandLabel,
  canExpand,
  onExpand,
}: {
  title: string;
  hint?: string;
  expandLabel?: string;
  canExpand?: boolean;
  onExpand?: () => void;
}) {
  const [tipOpen, setTipOpen] = useState(false);
  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      spacing={1}
      sx={{ mb: 1.25 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
          {title}
        </Typography>
        {hint ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.25, lineHeight: 1.35 }}
          >
            {hint}
          </Typography>
        ) : null}
      </Box>
      {canExpand && expandLabel && onExpand ? (
        <Tooltip
          title={expandLabel}
          open={tipOpen}
          onOpen={() => setTipOpen(true)}
          onClose={() => setTipOpen(false)}
        >
          <IconButton
            size="small"
            aria-label={expandLabel}
            onClick={() => {
              setTipOpen(false);
              onExpand();
            }}
            sx={{
              mt: 0.125,
              mr: -0.25,
              p: 0.25,
              width: 20,
              height: 20,
            }}
          >
            <OpenInFullIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Stack>
  );
}
